// =============================================================================
// VandieZ agent-web -- Accept Invitation page (/accept?token=...)
// =============================================================================
// Three-step flow for accepting an owner's agent invitation:
//
//   Step "preview": On mount, fetch the invitation via agentsApi.preview(token).
//                   Show owner name, invitee masked phone, granted permissions,
//                   and expiry. "Get OTP" button advances to the next step.
//
//   Step "otp":     authApi.requestOtp is called with the masked phone from the
//                   preview. Backend resolves the canonical phone from the token.
//                   User enters the 6-digit code and submits. On 422 with
//                   FULL_NAME_REQUIRED_FOR_NEW_USER, advance to "name" step.
//                   On success, setSession() + redirect to /.
//
//   Step "name":    New-user signup path. User enters their full name and submits
//                   agentsApi.accept again with the same session_id + code +
//                   full_name. The backend peeks before consuming so retrying
//                   is safe. On success, setSession() + redirect to /.
//
// If the user is already authenticated, redirect straight to /.
// =============================================================================

import { AlertCircle, CheckCircle2, Clock, Loader2, ShieldCheck } from "lucide-react";
import type React from "react";
import { useEffect, useState } from "react";
import { Navigate, useNavigate, useSearchParams } from "react-router-dom";

import { agentsApi, authApi } from "@/api/client";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/lib/auth";
import type { AgentInvitationPreview } from "@/types/agent";
import { ApiError } from "@/types/auth";

type Step = "preview" | "otp" | "name";

function formatExpiry(isoString: string): string {
  try {
    return new Date(isoString).toLocaleString("en-IN", {
      dateStyle: "medium",
      timeStyle: "short",
    });
  } catch {
    return isoString;
  }
}

function PermissionsSummary({
  permissions,
}: {
  permissions: AgentInvitationPreview["permissions"];
}): React.JSX.Element {
  const entries = Object.entries(permissions).filter(
    ([, v]) => Array.isArray(v) && (v as string[]).length > 0,
  );
  return (
    <ul className="space-y-1 text-sm">
      {entries.map(([domain, actions]) => (
        <li key={domain} className="flex items-center gap-2">
          <CheckCircle2 className="h-3.5 w-3.5 shrink-0 text-success" />
          <span className="font-medium capitalize">{domain}:</span>
          <span className="text-muted-foreground">
            {(actions as string[]).join(", ")}
          </span>
        </li>
      ))}
    </ul>
  );
}

export function AcceptInvitationPage(): React.JSX.Element {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const { user, setSession } = useAuth();
  const navigate = useNavigate();

  const [preview, setPreview] = useState<AgentInvitationPreview | null>(null);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [previewLoading, setPreviewLoading] = useState(true);
  const [step, setStep] = useState<Step>("preview");
  const [otpSessionId, setOtpSessionId] = useState<string | null>(null);
  const [maskedPhone, setMaskedPhone] = useState<string | null>(null);
  const [otpCode, setOtpCode] = useState("");
  const [fullName, setFullName] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch invitation preview on mount.
  useEffect(() => {
    if (token === null || token === "") {
      setPreviewError("Missing invitation token. Ask the owner to resend the link.");
      setPreviewLoading(false);
      return;
    }
    const controller = new AbortController();
    setPreviewLoading(true);
    agentsApi
      .preview(token, controller.signal)
      .then((data) => {
        setPreview(data);
        setPreviewLoading(false);
      })
      .catch((err: unknown) => {
        if (controller.signal.aborted) {return;}
        if (err instanceof ApiError && err.status === 404) {
          setPreviewError("Invitation not found or has expired. Ask the owner to resend.");
        } else {
          setPreviewError(
            err instanceof ApiError ? err.problem.detail : "Couldn't load invitation.",
          );
        }
        setPreviewLoading(false);
      });
    return () => {
      controller.abort();
    };
  }, [token]);

  // Already authenticated — go home.
  if (user !== null) {
    return <Navigate to="/" replace />;
  }

  // ── Step handlers ────────────────────────────────────────────────────────

  async function handleGetOtp(): Promise<void> {
    if (preview === null || token === null) {return;}
    setSubmitting(true);
    setError(null);
    try {
      // The backend resolves the canonical phone from the token, so we send
      // the masked phone string as-is. This is MVP-1 behaviour: the /accept
      // endpoint knows the real phone; the OTP is bound to the invitation.
      const resp = await authApi.requestOtp({
        phone: preview.invitee_phone_masked,
        purpose: "signup",
      });
      setOtpSessionId(resp.otp_session_id);
      setMaskedPhone(resp.masked_phone);
      setStep("otp");
    } catch (err) {
      setError(err instanceof ApiError ? err.problem.detail : "Couldn't send OTP.");
    } finally {
      setSubmitting(false);
    }
  }

  async function handleSubmitOtp(e: React.FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    if (token === null || otpSessionId === null) {return;}
    if (!/^\d{4,8}$/.test(otpCode)) {
      setError("Enter the OTP code (digits only).");
      return;
    }
    await callAccept();
  }

  async function handleSubmitName(e: React.FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    if (fullName.trim().length === 0) {
      setError("Enter your full name to create the account.");
      return;
    }
    await callAccept();
  }

  // Shared accept call — used by both "otp" and "name" steps.
  // exactOptionalPropertyTypes: pass full_name only when it has content.
  async function callAccept(): Promise<void> {
    if (token === null || otpSessionId === null) {return;}
    setSubmitting(true);
    setError(null);
    try {
      const resp = await agentsApi.accept({
        token,
        otp_session_id: otpSessionId,
        otp_code: otpCode,
        ...(fullName.trim().length > 0 ? { full_name: fullName.trim() } : {}),
      });
      setSession({
        user: resp.user,
        accessToken: resp.access_token,
        refreshToken: resp.refresh_token,
      });
      navigate("/", { replace: true });
    } catch (err) {
      if (
        err instanceof ApiError &&
        err.problem.status === 422 &&
        err.problem.code === "FULL_NAME_REQUIRED_FOR_NEW_USER"
      ) {
        setStep("name");
        setError(null);
        return;
      }
      setError(err instanceof ApiError ? err.problem.detail : "Couldn't accept invitation.");
    } finally {
      setSubmitting(false);
    }
  }

  // ── Card description per step ────────────────────────────────────────────

  function cardDescription(): string {
    if (previewLoading) {return "Loading invitation…";}
    if (previewError !== null) {return previewError;}
    if (preview === null) {return "";}
    switch (step) {
      case "preview":
        return `${preview.owner_full_name} has invited you to be their booking agent.`;
      case "otp":
        return `Enter the 6-digit code sent to ${maskedPhone ?? preview.invitee_phone_masked}.`;
      case "name":
        return "Enter your full name to create your agent account.";
    }
  }

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div className="flex min-h-screen items-center justify-center bg-muted/40 p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="space-y-2">
          <p className="text-xs uppercase tracking-widest text-muted-foreground">
            VandieZ Agent
          </p>
          <CardTitle>Accept agent invitation</CardTitle>
          <CardDescription>{cardDescription()}</CardDescription>
        </CardHeader>

        {/* ── Loading state ── */}
        {previewLoading ? (
          <CardContent className="flex justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </CardContent>
        ) : previewError !== null ? (
          <CardContent>
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{previewError}</AlertDescription>
            </Alert>
          </CardContent>
        ) : preview !== null && step === "preview" ? (
          /* ── Preview step ── */
          <>
            <CardContent className="space-y-5">
              <div className="rounded-lg border bg-muted/30 p-4 space-y-3">
                <div className="flex items-start gap-3">
                  <ShieldCheck className="mt-0.5 h-5 w-5 shrink-0 text-success" />
                  <div>
                    <p className="text-sm font-medium">Invited by</p>
                    <p className="text-sm text-muted-foreground">{preview.owner_full_name}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Clock className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
                  <div>
                    <p className="text-sm font-medium">Expires</p>
                    <p className="text-sm text-muted-foreground">
                      {formatExpiry(preview.expires_at)}
                    </p>
                  </div>
                </div>
              </div>
              <div>
                <p className="mb-2 text-sm font-medium">Granted permissions</p>
                <PermissionsSummary permissions={preview.permissions} />
              </div>
              {error !== null ? (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              ) : null}
            </CardContent>
            <CardFooter>
              <Button
                type="button"
                className="w-full"
                disabled={submitting}
                onClick={() => {
                  void handleGetOtp();
                }}
              >
                {submitting ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Sending OTP…
                  </>
                ) : (
                  "Get OTP"
                )}
              </Button>
            </CardFooter>
          </>
        ) : step === "otp" ? (
          /* ── OTP step ── */
          <form onSubmit={(e) => { void handleSubmitOtp(e); }}>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="otp">One-time code</Label>
                <Input
                  id="otp"
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  pattern="\d*"
                  maxLength={8}
                  placeholder="123456"
                  value={otpCode}
                  onChange={(e) => {
                    setOtpCode(e.target.value.replace(/\D/g, ""));
                  }}
                  required
                  autoFocus
                  disabled={submitting}
                />
              </div>
              {error !== null ? (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              ) : null}
            </CardContent>
            <CardFooter>
              <Button type="submit" disabled={submitting} className="w-full">
                {submitting ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Verifying…
                  </>
                ) : (
                  "Verify & Accept"
                )}
              </Button>
            </CardFooter>
          </form>
        ) : step === "name" ? (
          /* ── Name step (new-user signup) ── */
          <form onSubmit={(e) => { void handleSubmitName(e); }}>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="name">Full name</Label>
                <Input
                  id="name"
                  type="text"
                  autoComplete="name"
                  placeholder="e.g. Anand Kumar"
                  value={fullName}
                  onChange={(e) => {
                    setFullName(e.target.value);
                  }}
                  required
                  autoFocus
                  disabled={submitting}
                />
                <p className="text-xs text-muted-foreground">
                  We only ask once, when creating your account.
                </p>
              </div>
              {error !== null ? (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              ) : null}
            </CardContent>
            <CardFooter>
              <Button type="submit" disabled={submitting} className="w-full">
                {submitting ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Creating account…
                  </>
                ) : (
                  "Create my account"
                )}
              </Button>
            </CardFooter>
          </form>
        ) : null}
      </Card>
    </div>
  );
}
